import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, Field, Modal, MoneyInput, StatusBadge, useToast } from "@cx/ui";
import type { BoletoPdfLink, BoletoView, CnabSettingsView, TitleDetail } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { PAYMENT_STATUS_LABEL, reviewLabel } from "../pix/labels";
import { BANK_STATUS_LABEL, BOLETO_BADGE, BOLETO_PROVIDER_LABEL, digitableLineLabel } from "./labels";

/**
 * Boleto registrado da conta a receber (T2.10, RF-FIN-04): gerar pela integração de pagamentos (Asaas; sandbox fora
 * de produção) ou pela carteira própria da conta (registro na remessa CNAB), linha digitável, PDF, cancelar e
 * acompanhar a baixa (atualiza sozinho enquanto há boleto aguardando).
 */
export function BoletoPanel({
  title,
  canUpdate,
  onPaid,
}: {
  title: TitleDetail;
  canUpdate: boolean;
  onPaid(): void;
}) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [canceling, setCanceling] = useState<BoletoView | null>(null);
  const key = ["boletos", companyId, title.id];
  const boletos = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: BoletoView[] }>(`/boletos?titleId=${title.id}`),
    refetchInterval: (q) =>
      q.state.data?.data.some(
        (b) =>
          (b.status === "active" && b.provider !== "bank_cnab") ||
          b.payments.some((p) => p.status === "received"),
      )
        ? 5000
        : false,
  });
  const list = boletos.data?.data ?? [];
  const paidCount = list.filter((b) => b.status === "paid").length;
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (!boletos.data) return;
    if (seen.current !== null && paidCount > seen.current) onPaid();
    seen.current = paidCount;
  }, [boletos.data, paidCount, onPaid]);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const post = (path: string, body?: unknown, idem = true) =>
    api<unknown>(path, {
      method: "POST",
      headers: idem ? { "Idempotency-Key": crypto.randomUUID() } : undefined,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const cancel = useMutation({
    mutationFn: (b: BoletoView) => post(`/boletos/${b.id}/cancel`),
    onSuccess: () => {
      toast("success", "Boleto cancelado");
      refresh();
    },
    onError,
  });
  const check = useMutation({
    mutationFn: (id: string) => post(`/boletos/${id}/refresh`, undefined, false),
    onSuccess: () => {
      toast("success", "Situação consultada no provedor");
      refresh();
      onPaid();
    },
    onError,
  });
  const simulate = useMutation({
    mutationFn: (id: string) => post(`/boletos/${id}/simulate-payment`, {}),
    onSuccess: () => {
      toast("success", "Pagamento simulado enviado");
      refresh();
    },
    onError,
  });
  const pdf = useMutation({
    mutationFn: (id: string) => api<BoletoPdfLink>(`/boletos/${id}/pdf`),
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener");
    },
    onError,
  });
  const live = list.find((b) => b.status === "active" || b.status === "creating");
  const open = title.status === "open" || title.status === "partial";

  return (
    <div className="box card mb-3">
      <div className="d-flex flex-wrap align-items-center gap-2 px-3 pt-3 pb-2">
        <h2 className="h6 mb-0 me-auto">Boleto</h2>
        {canUpdate && open && !live && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setGenerating(true)}>
            Gerar boleto
          </button>
        )}
      </div>
      {boletos.error && <p className="text-danger px-3">{errorMessage(boletos.error)}</p>}
      {list.length === 0 && !boletos.isLoading && (
        <p className="text-muted px-3 pb-3 mb-0">Nenhum boleto gerado para esta conta.</p>
      )}
      {list.map((b) => {
        const badge = BOLETO_BADGE[b.status];
        const usable = b.status === "active" && b.bankStatus !== "rejected";
        return (
          <div key={b.id} className="border-top px-3 py-3" data-testid="boleto">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
              {b.bankStatus && <span className="badge text-bg-light">{BANK_STATUS_LABEL[b.bankStatus]}</span>}
              <span>
                Valor <strong>{formatBRL(b.amountCents)}</strong>
                {b.interestCents + b.fineCents > 0 && (
                  <span className="text-muted">
                    {" "}
                    (inclui {formatBRL(b.interestCents + b.fineCents)} de juros e multa)
                  </span>
                )}
              </span>
              {b.dueDate && <span className="text-muted small">Vence em {formatDateBR(b.dueDate)}</span>}
              <span className="text-muted small">
                {BOLETO_PROVIDER_LABEL[b.provider]}
                {b.ourNumber && <> · nosso número {b.ourNumber}</>}
              </span>
            </div>
            {b.status === "failed" && b.errorMessage && <p className="text-danger mb-2">{b.errorMessage}</p>}
            {b.bankStatus === "rejected" && b.errorMessage && (
              <p className="text-danger mb-2">{b.errorMessage}</p>
            )}
            {b.digitableLine && b.status !== "canceled" && b.status !== "failed" && (
              <div className="mb-2" style={{ maxWidth: 640 }}>
                <label className="form-label" htmlFor={`linha-${b.id}`}>
                  Linha digitável
                </label>
                <input
                  id={`linha-${b.id}`}
                  className="form-control font-monospace"
                  readOnly
                  value={digitableLineLabel(b.digitableLine)}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </div>
            )}
            <div className="d-flex flex-wrap gap-2">
              {b.digitableLine && usable && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(b.digitableLine!)
                      .then(() => toast("success", "Linha digitável copiada"))
                      .catch(() => toast("danger", "Não foi possível copiar; selecione o texto."))
                  }
                >
                  Copiar linha
                </button>
              )}
              {b.digitableLine && (b.status === "active" || b.status === "paid") && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={pdf.isPending}
                  onClick={() => pdf.mutate(b.id)}
                >
                  Imprimir boleto
                </button>
              )}
              {canUpdate && b.status === "active" && b.provider !== "bank_cnab" && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={check.isPending}
                  onClick={() => check.mutate(b.id)}
                >
                  Consultar no provedor
                </button>
              )}
              {canUpdate && b.status === "active" && b.provider === "sandbox" && (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  disabled={simulate.isPending}
                  onClick={() => simulate.mutate(b.id)}
                >
                  Simular pagamento
                </button>
              )}
              {canUpdate &&
                (b.status === "active" || b.status === "creating") &&
                b.bankStatus !== "cancel_requested" &&
                b.bankStatus !== "cancel_sent" && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={cancel.isPending}
                    onClick={() => setCanceling(b)}
                  >
                    Cancelar boleto
                  </button>
                )}
            </div>
            {b.payments.length > 0 && (
              <ul className="list-unstyled small mb-0 mt-2" aria-label="Pagamentos do boleto">
                {b.payments.map((p) => (
                  <li key={p.id}>
                    {formatDateBR(p.paidAt.slice(0, 10))} · {formatBRL(p.amountCents)} ·{" "}
                    {PAYMENT_STATUS_LABEL[p.status]}
                    {reviewLabel(p.reviewReason) && <> ({reviewLabel(p.reviewReason)})</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {generating && (
        <GenerateBoletoDialog title={title} onClose={() => setGenerating(false)} onCreated={refresh} />
      )}
      <ConfirmDialog
        open={!!canceling}
        title="Cancelar boleto"
        message={
          canceling?.provider === "bank_cnab" && canceling.bankStatus !== "pending"
            ? "Pedir a baixa deste boleto ao banco? O pedido vai na próxima remessa; até lá o boleto pode ser pago."
            : "Cancelar este boleto? Ele deixa de aceitar pagamento."
        }
        confirmLabel="Cancelar boleto"
        onConfirm={() => {
          if (canceling) cancel.mutate(canceling);
          setCanceling(null);
        }}
        onCancel={() => setCanceling(null)}
      />
    </div>
  );
}

function GenerateBoletoDialog({
  title,
  onClose,
  onCreated,
}: {
  title: TitleDetail;
  onClose(): void;
  onCreated(): void;
}) {
  const { companyId, me, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [amount, setAmount] = useState<number | null>(title.openCents);
  // "integration" ou o id da conta com carteira própria.
  const [via, setVia] = useState("integration");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const accounts = useQuery({
    queryKey: ["cnab-accounts", companyId],
    enabled: can(me.permissions as Permissions, "cnab", "read"),
    queryFn: () => api<{ data: CnabSettingsView[] }>("/cnab/accounts"),
  });
  const save = useMutation({
    mutationFn: () =>
      api<BoletoView>("/boletos", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          titleId: title.id,
          via: via === "integration" ? "integration" : "bank",
          financialAccountId: via === "integration" ? null : via,
          amountCents: amount,
        }),
      }),
    onSuccess: () => {
      toast("success", "Boleto gerado");
      onCreated();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
      onCreated();
    },
  });
  return (
    <Modal
      open={open}
      title="Gerar boleto"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Gerar boleto
          </button>
        </>
      }
    >
      <div
        className="row"
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <Field
          label="Registrar por"
          className="col-12"
          error={errors.financialAccountId ?? errors.via}
          hint="Carteira própria: o boleto vai ao banco na próxima remessa (Finanças › Remessas e retornos)."
        >
          <select className="form-select" value={via} onChange={(e) => setVia(e.target.value)}>
            <option value="integration">Integração de pagamentos (Asaas)</option>
            {(accounts.data?.data ?? []).map((a) => (
              <option key={a.financialAccountId} value={a.financialAccountId}>
                Carteira própria · {a.accountName}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Valor"
          required
          className="col-md-6"
          error={errors.amountCents}
          hint={`Saldo em aberto: ${formatBRL(title.openCents)}. Vencida: vencimento em 3 dias com juros e multa.`}
        >
          <MoneyInput value={amount} onChange={setAmount} />
        </Field>
      </div>
    </Modal>
  );
}
